import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import { ALLOWED_IFRAME_ORIGINS, CURRENT_DRAFT_YEAR, TDraftComponents } from '@/constants';
import { fetchProspects } from '@/request';

export const useAppStore = defineStore('app', () => {
  const bigBoardHeight = ref(0);
  const combineStatsHeight = ref(0);
  const draftMatrixHeight = ref(0);
  const playerStatsHeight = ref(0);
  const scoutsHeight = ref(0);
  const strapiPlayers = ref(null);
  const showSignup = ref(true);

  const initialLoadComplete = ref(true);

  const router = useRouter();

  const bigBoardTopFive = computed(() => {
    // sort the strapiPlayers by rank and return the top 5 players
    if (!strapiPlayers.value) return [];
    return strapiPlayers.value
      .filter((player: any) => player.class === `Draft Class ${CURRENT_DRAFT_YEAR}`)
      .slice()
      .sort((a: any, b: any) => b.grade - a.grade)
      .slice(0, 5);
  });

  const handleAppAction = (message: IPostMessage) => {
    if (message.action === 'height') {
      if (message.app === TDraftComponents.BIG_BOARD) {
        bigBoardHeight.value = message.value as number;
      }
      if (message.app === TDraftComponents.MATRIX) {
        draftMatrixHeight.value = message.value as number;
      }
      if (message.app === TDraftComponents.COMBINE_STATS) {
        combineStatsHeight.value = message.value as number;
      }
      if (message.app === TDraftComponents.PLAYER_STATS) {
        playerStatsHeight.value = message.value as number;
      }
      if (message.app === TDraftComponents.SCOUTS) {
        scoutsHeight.value = message.value as number;
      }
    }
    if (message.action === 'player') {
      // navigate to the Players route with the player id as a query param
      const playerId = message.value;
      router.push({ name: 'Players', params: { id: playerId } });
    }
    if (message.action === 'url') {
      // navigate to the Route whose name matches the URL in the message
      const url = String(message.value);
      if (url.includes('#')) {
        // if the url contains a hash, use it to navigate to a specific part of the page
        const hashIndex = url.indexOf('#');
        const baseUrl = url.substring(0, hashIndex); // get the base URL before the hash
        const hash = url.substring(hashIndex); // get the hash part
        router.push({ name: baseUrl, hash }); // navigate to the base URL with the hash
      } else {
        router.push({ name: url }); // navigate to the base URL directly
      }
    }
    if (message.action === 'glossary') {
      // navigate to the Glossary route with the term as a query param
      const term = `#${message.value}`;

      const routeData = router.resolve({ name: 'Glossary' });
      window.open(`${routeData.href}#${message.value}`, '_blank');
    }
    if (message.action === 'scout') {
      // navigate to the Scouts route with the scout id as a query param
      const scout = `#${message.value}`;
      const routeData = router.resolve({ name: 'Scouts' });
      window.open(`${routeData.href}#${message.value}`, '_blank');
    }
  };

  async function fetchStrapiPlayers(page: number = 1) {
    initialLoadComplete.value = false;
    const response = await fetchProspects(page);
    const responsePlayers = response.data.map((player: any) => ({
      id: player.id,
      name: player.attributes.Name,
      position: player.attributes.Position,
      team: player.attributes.team?.data?.attributes?.Abbreviation ?? '-',
      teamId: player.attributes.team?.data?.attributes?.TeamId ?? null,
      rank: player.attributes.OverallRank,
      grade: player.attributes.OverallGrade,
      class: player.attributes.DraftClass,
    }));

    if (!strapiPlayers.value) {
      strapiPlayers.value = [...responsePlayers];
    } else {
      strapiPlayers.value.push(...responsePlayers);
    }

    // check if there are more pages of data\
    const totalPages = response.meta.pagination.pageCount;
    const currentPage = response.meta.pagination.page;
    if (currentPage < totalPages) {
      await fetchStrapiPlayers(currentPage + 1);
    }
    initialLoadComplete.value = true;
  }

  const receivePostMessage = (event: MessageEvent) => {
    if (!ALLOWED_IFRAME_ORIGINS.includes(event.origin)) {
      return;
    }
    const message = JSON.parse(event.data);
    if (message) {
      handleAppAction(message);
    }
  };

  const setShowSignup = (value: boolean) => {
    // set local storage to remember the value
    localStorage.setItem('showSignup', JSON.stringify(value));
    // set the value in the store
    showSignup.value = value;
  };

  return {
    bigBoardHeight,
    bigBoardTopFive,
    combineStatsHeight,
    draftMatrixHeight,
    initialLoadComplete,
    playerStatsHeight,
    scoutsHeight,
    strapiPlayers,
    showSignup,
    fetchStrapiPlayers,
    receivePostMessage,
    setShowSignup,
  };
});
